// ─────────────────────────────────────────────
// KLOTHIQ — In-App Notification Popover
// ─────────────────────────────────────────────

import { useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Bell, CheckCheck, ExternalLink, Inbox } from 'lucide-react';
import {
  useInAppNotifications,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  type InAppNotificationItem,
} from '@/hooks/useTasks';

function formatRelativeTime(dateStr: string): string {
  try {
    const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

export default function NotificationPopover() {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  const { data, isLoading, isError, refetch } = useInAppNotifications();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const notifications = data?.notifications || [];
  const unreadCount = data?.unreadCount || 0;

  const handleNotificationClick = (item: InAppNotificationItem) => {
    if (!item.is_read) {
      markRead.mutate(item.id, {
        onError: () => toast.error('Unable to mark notification read. Try again.'),
      });
    }
    setIsOpen(false);
    if (item.link?.startsWith('/') && !item.link.startsWith('//')) {
      const taskId = item.link.match(/[?&]taskId=([0-9a-f-]{36})/i)?.[1];
      navigate(taskId ? `/my-tasks?taskId=${taskId}` : item.link);
    }
  };

  return (
    <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className="relative flex h-11 w-11 items-center justify-center rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          title="Notifications"
          aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ''}`}
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground shadow-sm animate-pulse">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </Popover.Trigger>

      {/* Escape the header's backdrop-filter stacking context above page cards. */}
      <Popover.Portal>
        <Popover.Content
          aria-label="Notifications"
          side="bottom"
          align="end"
          sideOffset={10}
          collisionPadding={12}
          className="z-50 flex max-h-[min(28rem,var(--radix-popover-content-available-height))] w-[min(24rem,calc(100vw-1.5rem))] flex-col overflow-hidden rounded-xl border border-border bg-card shadow-2xl outline-none animate-in fade-in zoom-in-95 duration-150"
        >
          {/* Header */}
          <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border px-4 py-3 bg-muted/30">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-sm text-foreground">Notifications</span>
              {unreadCount > 0 && (
                <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[11px] font-medium text-primary">
                  {unreadCount} new
                </span>
              )}
            </div>

            {unreadCount > 0 && (
              <button
                onClick={() =>
                  markAllRead.mutate(undefined, {
                    onError: () => toast.error('Unable to mark notifications read. Try again.'),
                  })
                }
                disabled={markAllRead.isPending}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
              >
                <CheckCheck className="h-3.5 w-3.5" />
                <span>Mark all read</span>
              </button>
            )}
          </div>

          {/* List */}
          <div className="min-h-0 overflow-y-auto overscroll-contain divide-y divide-border/50">
            {isError ? (
              <div role="alert" className="p-4 text-sm text-muted-foreground">
                <p>Notifications could not be loaded.</p>
                <button className="mt-2 min-h-11 text-primary" onClick={() => refetch()}>
                  Retry
                </button>
              </div>
            ) : isLoading ? (
              <div className="py-8 text-center text-xs text-muted-foreground">
                Loading notifications...
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
                <Inbox className="h-8 w-8 text-muted-foreground/40 mb-2" />
                <p className="text-sm font-medium text-foreground">No notifications</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  You're all caught up with your tasks
                </p>
              </div>
            ) : (
              notifications.map((item) => (
                <div
                  key={item.id}
                  onClick={() => handleNotificationClick(item)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleNotificationClick(item);
                    }
                  }}
                  className={`group flex items-start gap-3 p-3.5 cursor-pointer transition-colors ${
                    item.is_read
                      ? 'bg-card/50 hover:bg-muted/40'
                      : 'bg-primary/5 hover:bg-primary/10'
                  }`}
                >
                  <div className="mt-1">
                    {!item.is_read ? (
                      <span className="block h-2 w-2 rounded-full bg-primary ring-2 ring-primary/20" />
                    ) : (
                      <span className="block h-2 w-2 rounded-full bg-transparent" />
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <p
                      className={`text-xs ${item.is_read ? 'font-medium text-foreground/90' : 'font-semibold text-foreground'}`}
                    >
                      {item.title}
                    </p>
                    <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                      {item.message}
                    </p>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="text-[10px] text-muted-foreground/70">
                        {formatRelativeTime(item.created_at)}
                      </span>
                      {item.link && (
                        <span className="flex items-center gap-0.5 text-[10px] text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                          View <ExternalLink className="h-2.5 w-2.5" />
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
